import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik, Form } from "formik";
import axios from "axios";
import * as Yup from "yup";
import { TextField, Button, Typography, Grid, Box, Link } from "@mui/material";
import "./validate-otp.css";
import Loading from "../../../common/loader/loading";

const ValidateOTP = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isOtpComplete, setIsOtpComplete] = useState(false); // New state for OTP validation
  const navigate = useNavigate();
  const location = useLocation();

  // Ensure email exists in the location state
  const email = location?.state?.email || null;

  useEffect(() => {
    // If email is not provided, redirect the user or set an error
    if (!email) {
      setError("Email not provided.");
      setLoading(false);
      return;
    }

    const fetchUserData = async () => {
      try {
        const response = await axios.get(`/api/auth/users/${email}`);
        setUserData(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchUserData();
  }, [email]);

  // Loading state
  if (loading) {
    return <Loading />;
  }

  // Validation schema for Formik
  const validationSchema = Yup.object().shape({
    code1: Yup.string().required("Required").length(1, "Must be 1 digit"),
    code2: Yup.string().required("Required").length(1, "Must be 1 digit"),
    code3: Yup.string().required("Required").length(1, "Must be 1 digit"),
    code4: Yup.string().required("Required").length(1, "Must be 1 digit"),
    code5: Yup.string().required("Required").length(1, "Must be 1 digit"),
    code6: Yup.string().required("Required").length(1, "Must be 1 digit"),
  });

  // Check if all fields are filled to enable the button
  const handleOtpChange = (values) => {
    const allFilled = Object.values(values).every((val) => val.length === 1);
    setIsOtpComplete(allFilled);
  };

  const handleValidateOTP = async (values, { setSubmitting }) => {
    try {
      const token = userData?.resetpasswordtoken;
      const code = values.code1 + values.code2 + values.code3 + values.code4 + values.code5 + values.code6;
      await axios.post(
        "/api/auth/validate-otp",
        { otp: code, token, email },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      navigate("/reset-password", { state: { email } });
    } catch (err) {
      setError("Incorrect OTP. Please enter the code again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Grid container className='otp-container'>
      <Grid item xs={12} sm={8} md={4}>
        <Box className='otps-form' p={4}>
          <img src='/images/otp.png' alt='' className='otp' />
          <Typography variant='h5' gutterBottom>
            Enter Confirmation Code
          </Typography>
          <Typography gutterBottom>We sent a 6-digit code to {email}</Typography>
          <Formik
            initialValues={{ code1: "", code2: "", code3: "", code4: "", code5: "", code6: "" }}
            validationSchema={validationSchema}
            onSubmit={handleValidateOTP}
            validate={handleOtpChange} // Validate and check OTP completeness on change
          >
            {({ isSubmitting, handleChange, values, errors, touched }) => (
              <Form className='form-details'>
                <Grid container spacing={2} justifyContent='center'>
                  {["code1", "code2", "code3", "code4", "code5", "code6"].map((field, index) => (
                    <Grid item key={index}>
                      <TextField
                        name={field}
                        variant='outlined'
                        onChange={handleChange}
                        inputProps={{ maxLength: 1, style: { textAlign: "center" } }}
                        error={touched[field] && !!errors[field]}
                        helperText={touched[field] && errors[field]}
                        autoComplete='off' // Disable autocomplete
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white", // Your desired background color
                          },
                          width: "50px",
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
                {error && (
                  <Typography color='error' align='center' mt={2}>
                    {error}
                  </Typography>
                )}
                <Grid mt={2} className='otp-button'>
                  {/* Disable or hide the button if OTP is not complete */}
                  <Button
                    fullWidth
                    variant='contained'
                    color='primary'
                    type='submit'
                    disabled={isSubmitting || !isOtpComplete} // Disable the button until OTP is complete
                  >
                    {isSubmitting ? "Validating..." : "Validate OTP"}
                  </Button>
                </Grid>

                <Grid container justifyContent='center' mt={2}>
                  <Grid item>
                    <Typography variant='body2'>
                      Didn’t receive the Email?{" "}
                      <Link href='#' onClick={() => navigate("/request-reset-password")}>
                        Click to resend
                      </Link>
                    </Typography>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ValidateOTP;
